import { useState, useEffect, useMemo } from 'react';
import { supabase, Restaurant, MenuItem } from '../lib/supabase';
import { ShoppingCart, Plus, ChevronRight, UtensilsCrossed } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

type SizeOption = 'liten' | 'medium' | 'stor';
type MenuItemPrice = { size: SizeOption; price: number };

type MenuItemWithPrices = MenuItem & {
  menu_item_prices?: MenuItemPrice[];
};

export default function FoodPage() {
  const navigate = useNavigate();
  const [restaurants, setRestaurants] = useState<Restaurant[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadRestaurants();
  }, []);

  const loadRestaurants = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('restaurants')
      .select('*')
      .eq('is_active', true)
      .order('name', { ascending: true });

    if (!error) setRestaurants(data || []);
    setLoading(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen text-gray-500 dark:text-gray-400">
        Laster...
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors px-4 py-6">
      <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">Mat</h1>
      <p className="text-gray-600 dark:text-gray-400 mb-6">
        Velg restaurant og bestill favorittmaten din
      </p>

      {restaurants.length === 0 ? (
        <div className="text-center py-12">
          <UtensilsCrossed className="w-16 h-16 text-gray-300 dark:text-gray-700 mx-auto mb-4" />
          <p className="text-gray-500 dark:text-gray-400">
            Ingen restauranter tilgjengelige akkurat nå
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {restaurants.map((r) => (
            <button
              key={r.id}
              onClick={() => navigate(`/restaurant/${r.id}`)}
              className="w-full bg-white dark:bg-gray-800 rounded-2xl p-4 shadow-sm hover:shadow-md transition-all active:scale-[0.98] flex items-center gap-4"
            >
              {r.logo_url ? (
                <img
                  src={r.logo_url}
                  alt={r.name}
                  className="w-16 h-16 object-contain rounded-xl bg-gray-100 dark:bg-gray-700"
                />
              ) : (
                <div className="w-16 h-16 rounded-xl bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                  <UtensilsCrossed className="w-8 h-8 text-[#004D99] dark:text-orange-400" />
                </div>
              )}
              <div className="flex-1 text-left">
                <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                  {r.name}
                </h3>
                {r.description && (
                  <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2">
                    {r.description}
                  </p>
                )}
              </div>
              <ChevronRight className="w-5 h-5 text-gray-400" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
